import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Autocomplete,
} from "@mui/material"

const EditJobDialog = ({ open, onClose, job, users, jobTypes, onSave }) => {
  const [editData, setEditData] = useState({
    JobId: "",
    fees: "",
    dueDate: "",
    assignedTo: "",
    secondaryUsers: [],   // <-- NOW ALWAYS IDs
    jobTypeId: "",
  })

  useEffect(() => {
    if (job) {
      setEditData({
        JobId: job.JobId || job._id,
        fees: job.fees || "",
        dueDate: job.dueDate
          ? new Date(job.dueDate).toISOString().split("T")[0]
          : "",
        assignedTo: job.assignedToId || "",
        secondaryUsers: job.secondaryUser?.map(u => u._id) || [], // convert to IDs
        jobTypeId: job.jobTypeId || "",
      })
    }
  }, [job])

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }))
  }

  // Convert Autocomplete values -> IDs
  const handleSecondaryUsersChange = (event, newValue) => {
    const filtered = newValue?.filter(u => u._id !== editData.assignedTo)

    handleChange(
      "secondaryUsers",
      filtered.map(u => u._id) // <-- store only IDs
    )
  }

  const handleRemoveSecondaryUser = (userId) => {
    handleChange(
      "secondaryUsers",
      editData?.secondaryUsers?.filter(id => id !== userId)
    )
  }

  const handleSave = () => {
    console.log("Saving final editData:", editData)
    onSave(editData)
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setEditData({
        JobId: "",
        fees: "",
        dueDate: "",
        assignedTo: "",
        secondaryUsers: [],
        jobTypeId: "",
      })
    }, 200)
  }

  // Autocomplete needs full objects → convert IDs → objects
  const selectedSecondaryUserObjects = users?.filter(u =>
    editData.secondaryUsers.includes(u._id)
  )

  // Exclude primary user from secondary user options
  const availableSecondaryUsers = users?.filter(
    (user) => user._id !== editData.assignedTo
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Job Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          
          {/* Fees */}
          <TextField
            label="Fees"
            type="number"
            value={editData.fees}
            onChange={(e) => handleChange("fees", e.target.value)}
            fullWidth
          />

          {/* Due Date */}
          <TextField
            label="Due Date"
            type="date"
            value={editData.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split("T")[0] }}
          />

          {/* Job Type */}
          <FormControl fullWidth>
            <InputLabel>Job Type</InputLabel>
            <Select
              value={editData.jobTypeId}
              onChange={(e) => handleChange("jobTypeId", e.target.value)}
              label="Job Type"
            >
              {jobTypes?.map((job) => (
                <MenuItem key={job._id} value={job._id}>
                  {job.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Primary User */}
          <FormControl fullWidth>
            <InputLabel>Primary Assigned To</InputLabel>
            <Select
              value={editData.assignedTo}
              onChange={(e) => {
                const newAssignedTo = e.target.value
                const updatedSecondary = editData?.secondaryUsers?.filter(
                  (id) => id !== newAssignedTo
                )
                handleChange("assignedTo", newAssignedTo)
                handleChange("secondaryUsers", updatedSecondary)
              }}
              label="Primary Assigned To"
            >
              {users?.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Secondary Users */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Secondary Users
            </Typography>

            <Autocomplete
              multiple
              options={availableSecondaryUsers}
              getOptionLabel={(option) => option.name}
              value={selectedSecondaryUserObjects}  // <-- full objects only for UI
              onChange={handleSecondaryUsersChange}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField {...params} placeholder="Add secondary users..." size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    size="small"
                    onDelete={() => handleRemoveSecondaryUser(option._id)}
                  />
                ))
              }
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {editData.secondaryUsers.length === 0
                ? "No secondary users assigned"
                : `${editData.secondaryUsers.length} secondary user(s) assigned`}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditJobDialog
